/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Breadcrumb, Card, Table } from "react-bootstrap";
import { Projects } from "../../Model/Projects/Projects";
import {
  DateFormatWithStartOFTheDay,
  DateWithoutTime,
} from "../../Util/DateFormat";
import { Download } from "react-bootstrap-icons";
import { useApi } from "../../Controller/ApiController";
import { VIEW_PROJECT_MANUFACTURES_QUOTATION_DATA } from "../../Util/PermissionList";
import { setToast } from "../../Util/Toast";
import CustomSkeleton from "../../Components/CustomSkeleton";
import { CapitalizeAndRemoveUnderscore } from "../../Util/CapitalizeAndRemoveUnderscore";
import CustomButton from "../../Components/Button/CustomButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { getLocalStorage } from "../../Util/LocalStorage";
import { getUser } from "../../Util/Auth";
import { useDateFormat } from "../../Util/InputDateFormate";
import { PRODUCT_ADD } from "../../Routes/Inventory/Product";

function ViewProject({ permissions }: any) {
  const { id }: any = useParams();
  const { apiGet }: any = useApi();
  const getPermissions: any = getLocalStorage("permissions");
  const canApprove = getPermissions.includes("approved-project");
  const canReject = getPermissions.includes("rejected-project");
  const user = getUser();
  const currentdate = new Date();
  const dateFormat = useDateFormat();
  const [project, setProject] = useState<any>(null);
  const [allQuotation, setAllQuotation] = useState<any>([]);

  const [loading, setLoading] = useState(true);
  const [loadingForQuotationDetails, setLoadingForQuotationDetails] =
    useState(true);
  const [page, setPage] = useState(1);
  const [hideShowMore, setHideShowMore] = useState(false); // To control the visibility of "Show More"
  const [approvalLoading, setApprovalLoading] = useState(false);
  const navigate = useNavigate();

  const getQuotation = async (id: any, pageNumber: any) => {
    try {
      const item = await apiGet(
        `projects/${id}/quotations-data?include=country,project,projectManufactures,quotation,organization&page=${pageNumber}`,
      );
      item && setLoadingForQuotationDetails(false);
      // Set hideShowMore based on whether there are more pages
      setHideShowMore(item.meta.current_page >= item.meta.last_page);
      return item.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        setToast("error", error.response.data.message);
      } else {
        setToast("error", "An error occurred while processing your request");
      }
    }
  };

  const getProject = async (itemId: any) => {
    try {
      const item = await Projects.$query()
        .with([
          "buyer",
          "productManufactures",
          "products",
          "productManufactures.supplier",
          "organization",
          "salesOwner",
          "products.ingredient",
          "products.concentration",
        ])
        .find(itemId);
      return item.$attributes;
    } catch (error) {
      setToast("error", "An error occurred while processing your request");
    }
  };

  const loadMoreQuotations = async () => {
    const nextPage = page + 1;
    setPage(nextPage);

    const moreQuotations = await getQuotation(id, nextPage);
    setAllQuotation([...allQuotation, ...moreQuotations]);
  };

  const fetchData = async () => {
    const data: any = await getProject(id);
    data && setLoading(false);
    setProject(data);
    const quotationData: any = await getQuotation(id, page);
    setAllQuotation(quotationData);
  };

  useEffect(() => {
    fetchData();
    //eslint-disable-next-line
  }, [id]);

  const GoBack = () => {
    navigate(-1);
  };

  const removeNullValues = (obj: any) => {
    return Object.fromEntries(
      Object.entries(obj).filter(
        ([_, value]) => value !== null && value !== undefined,
      ),
    );
  };

  const handleApproval = async (status: "approved" | "rejected") => {
    setApprovalLoading(true);
    try {
      const filteredProducts = (project?.products || [])
        .filter((products: any) => products !== null && products !== undefined)
        .map((products: any) => removeNullValues(products)); // remove null fields from each item
      const filterdProductManufactures = (project?.product_manufactures || [])
        .filter(
          (product_manufactures: any) =>
            product_manufactures !== null && product_manufactures !== undefined,
        )
        .map((product_manufactures: any) =>
          removeNullValues(product_manufactures),
        );
      let newFormData: any = {
        approve_status: status,
        approved_by_id: user.id,
        approve_date: DateFormatWithStartOFTheDay(
          currentdate,
          dateFormat?.replace("dd", "DD"),
        ),
      };
      if (filteredProducts.length > 0) {
        newFormData.products = filteredProducts;
      }
      if (filterdProductManufactures.length > 0) {
        newFormData.product_manufactures = filterdProductManufactures;
      }
      // remove null values from root-level fields
      newFormData = removeNullValues(newFormData);
      await (Projects.$query() as any).update(id, newFormData);
      fetchData();
      setToast("success", `Status updated to ${status}`);
    } catch (error: any) {
      if (error.response.data.message) {
        setToast("error", error.response.data.message);
      } else {
        setToast("error", `Failed to update approval status:${error}`);
      }
    } finally {
      setApprovalLoading(false);
    }
  };
  return (
    <div>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/projects">Projects</Breadcrumb.Item>
        <Breadcrumb.Item active className="fw-bold">
          View Project
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="d-flex justify-content-between">
        <h2>View Project</h2>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {project?.approve_status !== "approved" &&
            project?.approve_status !== "rejected" && (
              <div className="d-flex gap-2 bg-transparent mb-2">
                {canApprove && (
                  <button
                    className="btn btn-sm btn-success d-flex align-items-center gap-2"
                    onClick={() => handleApproval("approved")}
                    disabled={approvalLoading}
                  >
                    {approvalLoading ? (
                      <span>Loading...</span>
                    ) : (
                      <>
                        Approve <FontAwesomeIcon icon={faCheck} />
                      </>
                    )}
                  </button>
                )}

                {canReject && (
                  <button
                    className="btn btn-sm btn-danger d-flex align-items-center gap-2"
                    onClick={() => handleApproval("rejected")}
                    disabled={approvalLoading}
                  >
                    {approvalLoading ? (
                      <span>Loading...</span>
                    ) : (
                      <>
                        Reject <FontAwesomeIcon icon={faTimes} />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          {project?.approve_status === "approved" && (
            <button
              className="btn btn-sm btn-primary d-flex align-items-center gap-2"
              onClick={() => navigate(`${PRODUCT_ADD}?projectid=${id}`)}
            >
              Convert To Product
            </button>
          )}
          <CustomButton label={`Back`} variant="secondary" onClick={GoBack} />
        </div>
      </div>
      <div>
        <Card className="mb-3">
          <Card.Body className="p-0">
            <Card.Header>Project Details</Card.Header>
            {loading ? (
              <CustomSkeleton
                height={192}
                containerClassName={"d-grid"}
                customOuterStyle={{ padding: 0, height: "192px" }}
                customInnerStyle={{ borderRadius: "0 0 0.375rem 0.375rem" }}
              />
            ) : (
              <div className="p-3 d-flex">
                <div style={{ marginRight: "220px" }}>
                  <p>
                    <span style={{ fontWeight: "700" }}> Title: </span>{" "}
                    {project?.title}
                  </p>
                  <p>
                    <span style={{ fontWeight: "700" }}> Description: </span>{" "}
                    {project?.description}
                  </p>
                  <p>
                    <span style={{ fontWeight: "700" }}> Project Value: </span>{" "}
                    <span className="text-center">
                      {project?.project_value !== "undefined"
                        ? project?.project_value
                        : ""}
                    </span>
                  </p>
                  <p>
                    <span style={{ fontWeight: "700" }}>
                      {" "}
                      Expected Closed Date:{" "}
                    </span>{" "}
                    {DateWithoutTime(project?.expected_closed_date)}
                  </p>
                </div>
                <div style={{ marginRight: "220px" }}>
                  <p className="d-flex">
                    <span style={{ fontWeight: "700" }} className="pt-1">
                      Status:{" "}
                    </span>{" "}
                    <span className="ms-2">
                      <Badge
                        pill
                        bg={
                          project.project_status === "draft"
                            ? "secondary"
                            : project.project_status === "close"
                            ? "danger"
                            : "success"
                        }
                      >
                        {project?.project_status &&
                          typeof project.project_status === "string" &&
                          CapitalizeAndRemoveUnderscore(project.project_status)}
                      </Badge>
                    </span>
                  </p>
                  <p>
                    <span style={{ fontWeight: "700" }}>Buyer/Supplier: </span>{" "}
                    {project?.buyer ? project?.buyer?.company_name : ""}
                  </p>
                  <p>
                    <span style={{ fontWeight: "700" }}>Sales Owner: </span>{" "}
                    {project?.sales_owner.first_name +
                      " " +
                      project?.sales_owner.last_name}
                  </p>
                  <p>
                    <span style={{ fontWeight: "700" }} className="">
                      Dosage Form:
                    </span>{" "}
                    {project?.dosage_form}
                  </p>
                </div>
                <div style={{ marginRight: "220px" }}>
                  <p>
                    <span style={{ fontWeight: "700" }} className="me-2">
                      {" "}
                      Filling:{" "}
                    </span>{" "}
                    {project?.filling}
                  </p>
                  <p>
                    <span style={{ fontWeight: "700" }} className="me-2">
                      {" "}
                      Type of Filling:{" "}
                    </span>{" "}
                    {project?.type_of_filling}
                  </p>
                  <p>
                    <span>
                      <span style={{ fontWeight: "700" }} className="me-2">
                        Packaging Material:
                      </span>
                    </span>
                    <a
                      href={project.packaging_material}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Download style={{ fill: "blue" }} />
                    </a>
                  </p>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
        {/* Products Details */}
        <Card className="mb-3">
          <Card.Body className="p-0">
            <Card.Header>Product Content</Card.Header>
            {loading ? (
              <CustomSkeleton
                height={100}
                containerClassName={"d-grid"}
                customOuterStyle={{ padding: 0, height: "100px" }}
                customInnerStyle={{ borderRadius: "0 0 0.375rem 0.375rem" }}
              />
            ) : (
              <Table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Ingredients</th>
                    <th>Concentration</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {project?.products.map((product: any, index: number) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{product.ingredient.name}</td>
                      <td>{product.concentration?.name}</td>
                      <td>
                        {(product.comments && product.comments) !== "null"
                          ? product.comments
                          : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Manufacturers Details */}
        <Card className="mb-3">
          <Card.Body className="p-0">
            <Card.Header>Manufacturers Details</Card.Header>
            {loading ? (
              <CustomSkeleton
                height={100}
                containerClassName={"d-grid"}
                customOuterStyle={{ padding: 0, height: "100px" }}
                customInnerStyle={{ borderRadius: "0 0 0.375rem 0.375rem" }}
              />
            ) : (
              <Table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Supplier</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {project?.product_manufactures.map(
                    (manufacture: any, index: number) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{manufacture.supplier.company_name}</td>
                        <td>
                          {manufacture.project_manufactures_status
                            .split("_")
                            .map(
                              (word: any) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(" ")}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
        {permissions[VIEW_PROJECT_MANUFACTURES_QUOTATION_DATA] && (
          <Card className="mb-5" style={{ overflowX: "scroll" }}>
            <Card.Body className="p-0">
              <Card.Header>View All Quotation Details</Card.Header>
              {loadingForQuotationDetails ? (
                <CustomSkeleton
                  height={100}
                  containerClassName={"d-grid"}
                  customOuterStyle={{ padding: 0, height: "100px" }}
                  customInnerStyle={{ borderRadius: "0 0 0.375rem 0.375rem" }}
                />
              ) : (
                <>
                  <div className="table-responsive">
                    <Table className="table table-borderless w-max-content mb-2 bg-transparent p-3">
                      <thead className="bg-transparent">
                        <tr className="text-center">
                          <th style={{ width: "50px" }}>#</th>
                          <th style={{ width: "250px" }}>Company Name</th>
                          <th style={{ width: "200px" }}>Company Country</th>
                          <th style={{ width: "150px" }}>Pack Size</th>
                          <th style={{ width: "120px" }}>MOQ</th>
                          <th style={{ width: "120px" }}>Price</th>
                          <th style={{ width: "150px" }}>Lead Time</th>
                          <th style={{ width: "200px" }}>
                            Alternative Site/Company
                          </th>
                          <th style={{ width: "150px" }}>Dossier Content</th>
                          <th style={{ width: "150px" }}>Formula</th>
                          <th style={{ width: "150px" }}>Created At</th>
                          <th style={{ width: "350px" }}>Comment</th>{" "}
                          {/* Increased width */}
                        </tr>
                      </thead>
                      <tbody className="bg-transparent">
                        {allQuotation.length > 0 ? (
                          allQuotation.map((quotation: any, index: any) => (
                            <tr key={index}>
                              <td style={{ width: "50px" }}>{index + 1}</td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.manuf_company_name}
                                  readOnly
                                  style={{ width: "250px" }}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation?.country?.name}
                                  readOnly
                                  style={{ width: "200px" }}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.pack_size}
                                  readOnly
                                  style={{ width: "150px" }}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.MOQ}
                                  readOnly
                                  style={{ width: "120px" }}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.price}
                                  readOnly
                                  style={{ width: "120px" }}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.lead_time}
                                  readOnly
                                  style={{ width: "150px" }}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.alternative_company_site}
                                  readOnly
                                  style={{ width: "200px" }}
                                />
                              </td>
                              <td
                                className="text-center"
                                style={{ width: "150px" }}
                              >
                                <a
                                  href={quotation.dossier_content}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Download style={{ fill: "green" }} />
                                </a>
                              </td>
                              <td
                                className="text-center"
                                style={{ width: "150px" }}
                              >
                                <a
                                  href={quotation.formula}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Download style={{ fill: "green" }} />
                                </a>
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.created_at}
                                  readOnly
                                  style={{ width: "200px" }}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control border-0 rounded-0 border-bottom text-center"
                                  type="text"
                                  value={quotation.comment}
                                  readOnly
                                  style={{ width: "350px" }} // Increased width for comment
                                />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={11} className="text-center">
                              No Record Found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  {!hideShowMore && allQuotation.length > 0 && (
                    <p
                      style={{ cursor: "pointer", textAlign: "end" }}
                      className="text-primary me-3 mt-1"
                      onClick={loadMoreQuotations}
                    >
                      Show More...
                    </p>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
}

export default ViewProject;
